// dashboardHelpers.ts - Memoized calculation utilities
import { useMemo } from "react";
import type { Expense } from "../src/types/types";

export const getMonthlyMultiplier = (frequency: string) => {
  return frequency === "weekly"
    ? 4.33
    : frequency === "biweekly"
      ? 2.17
      : frequency === "monthly"
        ? 1
        : 0;
};

// ✅ Memoized wave calculations - FIXED: Now uses actual income sources
export const useWaveCalculations = (
  expenses: Expense[],
  wave1Amount: number,
  wave2Amount: number,
  wave3Amount: number,
  wave4Amount: number,
  incomeSources: any[],
  frequency: "weekly" | "biweekly" | "monthly"
) => {
  return useMemo(() => {
    const waveCount =
      frequency === "weekly" ? 4 : frequency === "biweekly" ? 2 : 1;

    // Step 1: Calculate totals and counts per wave (ALL expenses - paid and unpaid)
    const waveTotals = Array(waveCount).fill(0);
    const waveCounts = Array(waveCount).fill(0);

    expenses.forEach((exp) => {
      if (
        (exp.category === "Wave" || exp.category === "Paycheck") &&
        exp.wave
      ) {
        const waveIndex = exp.wave - 1;
        if (waveIndex >= 0 && waveIndex < waveCount) {
          waveTotals[waveIndex] += exp.price;
          waveCounts[waveIndex]++;
        }
      }
    });

    // Step 2: Calculate budget per wave based on ACTUAL income sources
    const waveBudgets = (() => {
      // ✅ FIX: Initialize ALL wave budgets based on frequency
      const budgets = Array(waveCount)
        .fill(0)
        .map((_, i) => {
          if (i === 0) return wave1Amount || 0;
          if (i === 1) return wave2Amount || 0;
          if (i === 2) return wave3Amount || 0;
          if (i === 3) return wave4Amount || 0;
          return 0;
        });

      // ✅ FIX: Add additional income sources using their wave-specific amounts
      incomeSources.forEach((source) => {
        if (source.id !== "main") {
          // Skip main wave income
          // Each income source has amount1, amount2, amount3, amount4 fields
          // Add them to the corresponding wave budgets
          if (waveCount >= 1 && source.amount1) {
            budgets[0] = (budgets[0] || 0) + (source.amount1 || 0);
          }
          if (waveCount >= 2 && source.amount2) {
            budgets[1] = (budgets[1] || 0) + (source.amount2 || 0);
          }
          if (waveCount >= 3 && source.amount3) {
            budgets[2] = (budgets[2] || 0) + (source.amount3 || 0);
          }
          if (waveCount >= 4 && source.amount4) {
            budgets[3] = (budgets[3] || 0) + (source.amount4 || 0);
          }
        }
      });

      return budgets;
    })();

    // Step 3: Calculate balances (budget - expenses per wave)
    const waveBalances = waveTotals.map((total, i) => waveBudgets[i] - total);

    // Step 4: Other expenses (ALL - paid and unpaid)
    const otherExpensesTotal = expenses
      .filter((e) => e.category === "Other")
      .reduce((sum, e) => sum + e.price, 0);

    // Step 5: Total wave expenses - COUNT of all wave expenses
    const totalWaveExpenses = expenses.filter(
      (e) => e.category === "Wave" || e.category === "Paycheck"
    ).length;

    // Step 6: Total wave AMOUNT - $ sum of all wave expenses (matching original)
    const totalWaveAmount = waveTotals.reduce((sum, total) => sum + total, 0);

    return {
      waveCount,
      waveTotals,
      waveCounts,
      waveBalances,
      otherExpensesTotal,
      totalWaveExpenses, // COUNT of wave expenses (number of items)
      totalWaveAmount, // $ TOTAL of wave expenses (original name)
      waveBudgets,
    };
  }, [
    expenses,
    wave1Amount,
    wave2Amount,
    wave3Amount,
    wave4Amount,
    frequency,
    incomeSources,
  ]); // ✅ FIX: Added wave3Amount, wave4Amount, and incomeSources dependency
};

// ✅ Memoized filtered and sorted expenses
export function useFilteredExpenses(
  expenses: Expense[],
  waveFilter: string,
  searchQuery: string,
  expenseListFrequency: string
) {
  return useMemo(() => {
    const filtered = expenses.filter((exp) => {
      // ✅ FIX: Add credit card filter and remove "always show Other"
      const matchesWave =
        waveFilter === "All" ||
        (waveFilter === "Credit Card" && exp.accountType === "credit") || // Show only credit card expenses
        ((exp.category === "Wave" ||
          exp.category === "Paycheck" ||
          exp.category === "Other" ||
          exp.category === "BNPL") &&
          exp.wave ===
            parseInt(waveFilter.replace("Week ", "").replace("Wave ", "")));
      const matchesSearch = exp.name
        .toLowerCase()
        .includes(searchQuery.toLowerCase());
      return matchesWave && matchesSearch;
    });

    return [...filtered].sort((a, b) => {
      const categoryOrderA =
        a.category === "Wave" || a.category === "Paycheck" ? 0 : 1;
      const categoryOrderB =
        b.category === "Wave" || b.category === "Paycheck" ? 0 : 1;
      if (categoryOrderA !== categoryOrderB) {
        return categoryOrderA - categoryOrderB;
      }
      if (!a.dueDate || !b.dueDate) return 0;
      const dayA = parseInt(a.dueDate.split("-")[2]);
      const dayB = parseInt(b.dueDate.split("-")[2]);
      return dayA - dayB;
    });
  }, [expenses, waveFilter, searchQuery, expenseListFrequency]);
}

// ✅ Memoized income sources with main wave
export const useAllIncomeSources = (
  incomeSources: any[],
  wave1Amount: number,
  wave2Amount: number,
  frequency: string,
  language: string,
  primaryBlue: string
) => {
  return useMemo(() => {
    const mainWave = {
      id: "main",
      name: language === "en" ? "Main Wave Income" : "Ingreso Wave Principal",
      amount: wave1Amount, // Use wave1 as primary
      wave2Amount: wave2Amount, // Store wave2 separately
      frequency: frequency,
      color: primaryBlue,
    };
    return [mainWave, ...incomeSources];
  }, [
    incomeSources,
    wave1Amount,
    wave2Amount,
    frequency,
    language,
    primaryBlue,
  ]);
};

// ✅ Memoized chart data for pie chart
export function useCategoryChartData(
  allIncomeSources: any[],
  expenses: Expense[]
) {
  return useMemo(
    () => ({
      labels: allIncomeSources.map((source) => source.name),
      datasets: [
        {
          data: allIncomeSources.map((source) => {
            const sourceExpenses = expenses.filter((e) => {
              if (source.id === "main") {
                return !e.incomeSourceId || e.incomeSourceId === "";
              }
              return e.incomeSourceId === source.id;
            });
            return sourceExpenses.reduce((sum, e) => sum + e.price, 0);
          }),
          backgroundColor: allIncomeSources.map(
            (source) => source.color + "CC"
          ),
          borderColor: allIncomeSources.map((source) => source.color),
          borderWidth: 2,
        },
      ],
    }),
    [allIncomeSources, expenses]
  );
}

// ✅ Memoized chart data for bar chart
export function useBalanceChartData(
  allIncomeSources: any[],
  expenses: Expense[],
  language: string
) {
  const balanceColors = useMemo(() => {
    return allIncomeSources.map((source) => {
      const monthly = source.amount * getMonthlyMultiplier(source.frequency);
      const spent = expenses
        .filter((e) =>
          source.id === "main"
            ? !e.incomeSourceId || e.incomeSourceId === ""
            : e.incomeSourceId === source.id
        )
        .reduce((s, e) => s + e.price, 0);
      return monthly >= spent
        ? "rgba(52, 199, 89, 0.8)"
        : "rgba(255, 59, 48, 0.8)";
    });
  }, [allIncomeSources, expenses]);

  const incomeColors = useMemo(() => {
    return allIncomeSources.map((source) => source.color + "80");
  }, [allIncomeSources]);

  return useMemo(
    () => ({
      labels: allIncomeSources.map((source) => source.name),
      datasets: [
        {
          label: language === "en" ? "Income" : "Ingreso",
          data: allIncomeSources.map(
            (source) => source.amount * getMonthlyMultiplier(source.frequency)
          ),
          backgroundColor: incomeColors,
          borderColor: allIncomeSources.map((s) => s.color),
          borderWidth: 2,
          borderRadius: 8,
        },
        {
          label: language === "en" ? "Expenses" : "Gastos",
          data: allIncomeSources.map((source) => {
            const sourceExpenses = expenses.filter((e) =>
              source.id === "main"
                ? !e.incomeSourceId || e.incomeSourceId === ""
                : e.incomeSourceId === source.id
            );
            return sourceExpenses.reduce((sum, e) => sum + e.price, 0);
          }),
          backgroundColor: allIncomeSources.map(() => "rgba(255, 149, 0, 0.6)"),
          borderColor: allIncomeSources.map(() => "rgba(255, 149, 0, 1)"),
          borderWidth: 2,
          borderRadius: 8,
        },
        {
          label: language === "en" ? "Balance" : "Balance",
          data: allIncomeSources.map((source) => {
            const monthlyIncome =
              source.amount * getMonthlyMultiplier(source.frequency);
            const sourceExpenses = expenses.filter((e) =>
              source.id === "main"
                ? !e.incomeSourceId || e.incomeSourceId === ""
                : e.incomeSourceId === source.id
            );
            const totalExpenses = sourceExpenses.reduce(
              (sum, e) => sum + e.price,
              0
            );
            return monthlyIncome - totalExpenses;
          }),
          backgroundColor: balanceColors,
          borderColor: balanceColors.map((c) => c.replace("0.8", "1")),
          borderWidth: 2,
          borderRadius: 8,
        },
      ],
    }),
    [allIncomeSources, expenses, language, incomeColors, balanceColors]
  );
}

export function useHasPaidExpenses(expenses: Expense[]) {
  return useMemo(() => expenses.some((e) => e.isPaid), [expenses]);
}

// Handle form input changes
export const handleExpenseFormChange = (
  e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  setNewExpense: (updater: (prev: any) => any) => void
) => {
  const { name, value } = e.target;
  setNewExpense((prev: any) => {
    let updatedIncomeSourceId = prev.incomeSourceId;

    if (name === "incomeSourceId") {
      updatedIncomeSourceId = value === "main-wave" ? "" : value;
    }

    const updated = {
      ...prev,
      [name]:
        name === "price"
          ? parseFloat(value) || 0
          : name === "wave"
            ? parseInt(value) || undefined
            : name === "category"
              ? (value as "Wave" | "Paycheck" | "Other")
              : name === "frequency"
                ? (value as "weekly" | "biweekly" | "monthly")
                : value,
      incomeSourceId: updatedIncomeSourceId,
      paymentType:
        name === "paymentType"
          ? (value as "url" | "app" | "autopay")
          : prev.paymentType,
    };

    if (name === "paymentType" && value === "autopay") {
      updated.paymentUrl = "";
    }

    // Auto-add https:// to payment URL if not present
    if (name === "paymentUrl" && value && prev.paymentType !== "autopay") {
      // Only add protocol if value doesn't already have one and contains a dot (looks like a domain)
      if (!value.match(/^[a-zA-Z]+:\/\//) && value.includes(".")) {
        updated.paymentUrl = "https://" + value;
      }
    }

    // Convert to app deep link when switching to app payment type
    if (name === "paymentType" && value === "app" && prev.paymentUrl) {
      const url = prev.paymentUrl.toLowerCase();
      // Common app deep link conversions
      if (url.includes("venmo")) {
        updated.paymentUrl = "venmo://";
      } else if (url.includes("cashapp") || url.includes("cash.app")) {
        updated.paymentUrl = "cashapp://";
      } else if (url.includes("zelle")) {
        updated.paymentUrl = "zelle://";
      } else if (url.includes("paypal")) {
        updated.paymentUrl = "paypal://";
      } else if (url.includes("chase")) {
        updated.paymentUrl = "chase://";
      } else if (url.includes("wellsfargo") || url.includes("wells")) {
        updated.paymentUrl = "wellsfargo://";
      } else if (url.includes("bankofamerica") || url.includes("bofa")) {
        updated.paymentUrl = "bankofamerica://";
      } else if (url.includes("citi") && !url.includes("city")) {
        updated.paymentUrl = "citi://";
      } else if (url.includes("capitalone")) {
        updated.paymentUrl = "capitalone://";
      }
      // If no match found, keep the original URL (user can manually set deep link)
    }

    if (name === "category" && value === "Other") {
      updated.wave = undefined;
      updated.frequency = "biweekly";
      updated.incomeSourceId = "";
    }
    if (
      name === "category" &&
      (value === "Wave" || value === "Paycheck") &&
      updated.wave === undefined
    ) {
      updated.wave = 1;
      if (!updated.incomeSourceId) {
        updated.incomeSourceId = "";
      }
    }
    if (name === "frequency" && updated.category === "Paycheck") {
      const maxWaves = value === "weekly" ? 4 : value === "biweekly" ? 2 : 1;
      if (updated.wave && updated.wave > maxWaves) {
        updated.wave = 1;
      }
    }
    return updated;
  });
};
